import { cdn } from "core/cdn"
import {
  CONSENT_PAGE,
  DIAGNOSTIC_DEVICES_PAGE,
  DOCTOR_CHOICE_PAGE,
  NIR_AND_EXEMPTION_PAGE,
  PHARMACIST_EMAIL_PAGE,
  PREVENTION_INTRO_PAGE,
  PREVENTION_REPORT_INFO,
  UPLOAD_DOC_PAGE,
  VITALS_PAGE,
  PREVENTION_HANDOFF_PAGE,
} from "core/constants"

import { ACTION, Question, QUESTION_TYPE, Reply, UiView } from "./types"
import { languages } from "locales/languages"
import { Gender, TreatingDoctorStatus } from "types/entity"

export const ITEMS_PER_CAROUSEL_PAGE = 6
export const SECOND_NEXT_ANSWER = 0.2
export const PATH_DEEP = ":"
export const PREVENTION_PATH_DEEP = "_"
export const DURATION_SLOT_MIN = 5
export const NUMBER_OF_SLOTS_BEFORE_NOW = 5
export const NUMBER_OF_SLOTS_AFTER_NOW = 5

export enum PATH {
  doctor_type = "doctor_type",
  patient = "patient",
  exemption = "exemption",
  doctor = "doctor",
  slot = "doctor:slot",
  inform = "inform",
  reason = "reason",
  vitals = "reason:vitals",
  illness = "illness",
  illness_details = "illness:details",
  allergy = "allergies",
  allergy_details = "allergies:details",
  operation = "operations",
  drug = "drugs",
  pregnant = "pregnant",
  ineligible = "ineligible",
  consent = "consent",
  diagnostic_devices = "diagnostic_devices",
  choice_of_doctor = "choice_of_doctor",
  doctor_available = "doctor_available",
  upload_documents = "upload_documents",

  selected_documents = "upload_documents:redirect",
  selected_documents_prevention = "upload_documents_prevention",
  selected_carer = "selected_carer",
  tle_pharmacist = "tle_pharmacist",

  // dermatology
  dermato_overview = "dermato_overview",
  dermato_reason = "reason",
  dermato_symptoms = "dermato_symptoms",
  dermato_body_zone = "dermato_body_zone",
  dermato_phototype = "dermato_phototype",
  dermato_video_capture = "dermato_video_capture",
  anamnesis_questions = "anamnesis_questions",
  dermato_questions = "dermato_questions",
  dermato_anamnesis_questions = "dermato_anamnesis_questions",
  dermato_last_control = "dermato_last_control",
  dermato_sun_exposure = "sun_exposure",
  dermato_sun_protection = "sun_protection",
  dermato_sunburn_before_20 = "sunburn_before_20",
  dermato_history = "dermato_history",
  dermato_history_details = "dermato_history:details",
  dermato_family_cancer = "dermato_family_cancer",
  dermato_recent_travel = "dermato_recent_travel",
  dermato_zone_evolution = "dermato_zone_evolution",
  dermato_problem_duration = "dermato_problem_duration",
  dermato_zone_evolution_duration = "dermato_zone_evolution_duration",
  dermato_ineligible = "dermato_ineligible",
  dermato_recap = "dermato_recap",
  dermato_pharmacist = "dermato_pharmacist",
  dermato_consent = "dermato_consent",
  dermato_overview_part1 = "dermato_overview_part1",
  dermato_overview_info = "dermato_overview_info",
  dermato_overview_part2 = "dermato_overview_part2",
  pharmacist_must_be_here = "pharmacist_must_be_here",

  // checkup
  checkup_overview = "checkup_overview",
  checkup_vitals = "checkup_vitals",
  checkup_report = "checkup_report",

}

export const booleanSubReplies: Reply[] = [
  {
    title: languages.yes,
    action: ACTION.subquestion,
    value: true,
    ui: {
      view: "button",
    },
  },
  {
    title: languages.no,
    value: false,
    ui: {
      view: "button",
    },
  },
]

export const booleanReplies: Reply[] = [
  {
    title: languages.yes,
    value: true,
    ui: {
      view: "button",
    },
  },
  {
    title: languages.no,
    value: false,
    ui: {
      view: "button",
    },
  },
]

export const consent: Question = {
  id: PATH.consent,
  title: "do-not-need-title-because-it-is-a-redirection-step",
  multipleReplies: false,
  replies: [],
  type: QUESTION_TYPE.redirect,
  value: CONSENT_PAGE,
}

export const doctor_available: Question = {
  title: languages.anamneseDoctorAvailableQuestion,
  id: PATH.doctor_available,
  multipleReplies: false,
  ui: { preventAddMore: true },
  replies: [
    {
      title: languages.doctorFarAway,
      value: TreatingDoctorStatus.DOCTOR_AWAY_CONSENT,
    },
    {
      title: languages.doNotHaveDoctor,
      value: TreatingDoctorStatus.NO_DOCTOR,
    },
    {
      title: languages.doctorNotAvailable,
      value: TreatingDoctorStatus.DOCTOR_UNAVAILABLE,
    },
  ],
}

export const choice_of_doctor: Question = {
  title: languages.anamneseChoiceOfDoctor,
  id: PATH.choice_of_doctor,
  type: QUESTION_TYPE.redirect,
  replies: [],
  value: DOCTOR_CHOICE_PAGE,
}

export const diagnostic_devices: Question = {
  title: "Testez votre matériel",
  id: PATH.diagnostic_devices,
  type: QUESTION_TYPE.redirect,
  replies: [],
  value: DIAGNOSTIC_DEVICES_PAGE,
}

export const ineligible_reason: Question = {
  title: languages.uneligibleTitle,
  id: PATH.ineligible,
  informationText: languages.ineligibleReasonInformation,
  multipleReplies: false,
  replies: [
    {
      imgUrl: cdn("/images/ineligible/picto_bebe.svg"),
      title: languages.lessthan3yearOld,
      value: "Les enfants de moins de 3 ans",
      action: ACTION.nothing,
    },
    {
      imgUrl: cdn("/images/ineligible/picto_ado.svg"),
      title: languages.unaccompaniedMinors,
      value: "Les mineurs non accompagnés",
      action: ACTION.nothing,
    },
    {
      imgUrl: cdn("/images/ineligible/picto_prolongation_arret_de_travail.svg"),
      title: languages.ExtendedSickLeave,
      value: "Prolongation d'arrêt de travail",
      action: ACTION.nothing,
    },
    {
      imgUrl: cdn("/images/ineligible/picto_certificat_sport.svg"),
      title: languages.fitnessCertificate,
      value: "Certificat d'aptitude (sport, crèche)",
      action: ACTION.nothing,
    },
    {
      imgUrl: cdn("/images/ineligible/picto_ordo.svg"),
      title: languages.PrescriptionOfPsychoactive,
      value: "Prescription de traitements psychoactifs de dépendance",
      action: ACTION.nothing,
    },
  ],
  defaultReply: {
    title: languages.IhaveUnderstood,
    value: true,
    ui: { type: "primary" },
  },
  ui: { view: "inline" as UiView },
}

export const reasonValues = {
  COVID: "Coronavirus (Covid 19)",
  ALLERGIES: "Allergies",
  BURN: "Brûlure",
  CONTRACEPTION: "Contraception",
  CYSTITIS: "Cystite",
  JOINT_PAIN: "Douleurs articulaires",
  TOOTH_PAIN: "Douleurs dentaires",
  DIARRHEA_OR_VOMITING: "Diarrhée ou vomissement",
  BACK_PAIN: "Douleurs lombaires",
  RASH: "Éruption cutanée",
  TIRED: "Fatigue / anxiété / stress",
  FEVER: "Fièvre",
  SEXUAL_INFECTION: "Infection sexuelle",
  SORE_THROAT: "Maux de gorge",
  HEADACHE: "Maux de tête",
  STOMACH_ACHE: "Maux de ventre",
  EYE_PAIN: "Œil rouge et/ou douleur",
  STING: "Piqûre / morsure / blessure",
  OTITIS: "Oreilles/otite",
  PRESCRIPTION_REFILL: "Renouvellement d'ordonnance",
  PERIOD_PAIN: "Règles douloureuses",
  LAB_RESULTS: "Résultats de laboratoire",
  COLD_AND_FLU: "Rhume et syndrome grippal",
  SINUS: "Sinus ou nez bouché",
  COUGH: "Toux",
  OTHER_REASON: "Autre",
}

export const vitals: Question = {
  id: PATH.vitals,
  title: "do-not-need-title-because-it-is-a-redirection-step",
  multipleReplies: false,
  replies: [],
  value: VITALS_PAGE,
  type: QUESTION_TYPE.redirect,
}

export const reason: Question = {
  title: languages.anamneseReasonQuestion,
  id: PATH.reason,
  multipleReplies: true,
  maxReplies: 3,
  ui: { view: "carousel" },
  replies: [
    {
      title: languages.sore_throat,
      value: reasonValues.SORE_THROAT,
      imgUrl: cdn("/images/reasons/picto_mal_de_gorge.svg"),
      action: ACTION.subquestion,
    },
    {
      title: languages.diarrhea_or_vomiting,
      value: reasonValues.DIARRHEA_OR_VOMITING,
      imgUrl: cdn("/images/reasons/picto_diarrhee.svg"),
      action: ACTION.subquestion,
    },
    {
      title: languages.cold_and_flu,
      value: reasonValues.COLD_AND_FLU,
      imgUrl: cdn("/images/reasons/picto_rhume.svg"),
      action: ACTION.subquestion,
    },
    {
      title: languages.fever,
      value: reasonValues.FEVER,
      imgUrl: cdn("/images/reasons/picto_fievre.svg"),
      action: ACTION.subquestion,
    },
    {
      title: languages.cystitis,
      value: reasonValues.CYSTITIS,
      imgUrl: cdn("/images/reasons/picto_cystite.svg"),
      action: ACTION.subquestion,
    },
    {
      title: languages.prescription_refill,
      value: reasonValues.PRESCRIPTION_REFILL,
      imgUrl: cdn("/images/reasons/picto_ordonnance.svg"),
      action: ACTION.subquestion,
    },
    {
      title: languages.jointpain,
      value: reasonValues.JOINT_PAIN,
      imgUrl: cdn("/images/reasons/picto_jointpain.svg"),
      action: ACTION.subquestion,
    },
    {
      title: languages.otitis,
      value: reasonValues.OTITIS,
      imgUrl: cdn("/images/reasons/picto_otitis.svg"),
      action: ACTION.subquestion,
    },
    {
      title: languages.cough,
      value: reasonValues.COUGH,
      imgUrl: cdn("/images/reasons/picto_toux.svg"),
      action: ACTION.subquestion,
    },
    {
      title: languages.headache,
      value: reasonValues.HEADACHE,
      imgUrl: cdn("/images/reasons/picto_mal_de_tete.svg"),
      action: ACTION.subquestion,
    },
    {
      title: languages.back_pain,
      value: reasonValues.BACK_PAIN,
      imgUrl: cdn("/images/reasons/picto_douleur_lombaire.svg"),
      action: ACTION.subquestion,
    },
    {
      title: languages.tired,
      value: reasonValues.TIRED,
      imgUrl: cdn("/images/reasons/picto_fatigue.svg"),
      action: ACTION.subquestion,
    },
    {
      title: languages.rash,
      value: reasonValues.RASH,
      imgUrl: cdn("/images/reasons/picto_eruption_cutanee.svg"),
      action: ACTION.subquestion,
    },
    {
      title: languages.stomach_ache,
      value: reasonValues.STOMACH_ACHE,
      imgUrl: cdn("/images/reasons/picto_maux_de_ventre.svg"),
      action: ACTION.subquestion,
    },
    {
      title: languages.allergy,
      value: reasonValues.ALLERGIES,
      imgUrl: cdn("/images/reasons/picto_allergies.svg"),
      action: ACTION.subquestion,
    },
    {
      title: languages.toothpain,
      value: reasonValues.TOOTH_PAIN,
      imgUrl: cdn("/images/reasons/picto_toothache.svg"),
      action: ACTION.subquestion,
    },
    {
      title: languages.eye_pain,
      value: reasonValues.EYE_PAIN,
      imgUrl: cdn("/images/reasons/picto_oeil_rouge_douleur.svg"),
      action: ACTION.subquestion,
    },
    {
      title: languages.sexual_infection,
      value: reasonValues.SEXUAL_INFECTION,
      imgUrl: cdn("/images/reasons/picto_infection_sexuelle.svg"),
      action: ACTION.subquestion,
    },
    {
      title: languages.sinus,
      value: reasonValues.SINUS,
      imgUrl: cdn("/images/reasons/picto_nez_bouche.svg"),
      action: ACTION.subquestion,
    },
    {
      title: languages.period_pain,
      value: reasonValues.PERIOD_PAIN,
      imgUrl: cdn("/images/reasons/picto_regles_douloureuse.svg"),
      action: ACTION.subquestion,
    },
    {
      title: languages.sting,
      value: reasonValues.STING,
      imgUrl: cdn("/images/reasons/picto_sting.svg"),
      action: ACTION.subquestion,
    },
    {
      title: languages.contraception,
      value: reasonValues.CONTRACEPTION,
      imgUrl: cdn("/images/reasons/picto_contraception.svg"),
      action: ACTION.subquestion,
    },
    {
      title: languages.lab_results,
      value: reasonValues.LAB_RESULTS,
      imgUrl: cdn("/images/reasons/picto_resultat_laboratoire.svg"),
      action: ACTION.subquestion,
    },
    {
      title: languages.burn,
      value: reasonValues.BURN,
      imgUrl: cdn("/images/reasons/picto_brulure.svg"),
      action: ACTION.subquestion,
    },
    {
      title: languages.otherReason,
      value: reasonValues.OTHER_REASON,
      imgUrl: cdn("/images/reasons/picto_autre.svg"),
      action: ACTION.subquestion,
    },
  ],
  subquestion: vitals,
}

export const exemption: Question = {
  id: PATH.exemption,
  title: "do-not-need-title-because-it-is-a-redirection-step",
  multipleReplies: false,
  replies: [],
  type: QUESTION_TYPE.redirect,
  value: NIR_AND_EXEMPTION_PAGE,
}

export const selected_documents: Question = {
  id: PATH.selected_documents,
  title: languages.uploadDocumentSelectOrAdd,
  multipleReplies: true,
  imgUrl: "",
  replies: [],
  defaultReply: {
    title: languages.uploadDocumentAddButton,
    action: ACTION.redirect,
    value: UPLOAD_DOC_PAGE,
  },
}

export const upload_documents: Question = {
  id: PATH.upload_documents,
  title: languages.uploadDocumentQuestion,
  subtitle: languages.uploadDocumentTypesHints,
  imgUrl: cdn("images/document_send_illustration.svg"),
  multipleReplies: false,
  replies: booleanSubReplies,
  subquestion: selected_documents,
}

const inform_anamnesis: Question = {
  title: languages.inform_anamnesis,
  imgUrl: cdn("/images/anamnese_illustration.svg"),
  id: PATH.inform,
  multipleReplies: false,
  replies: [],
  defaultReply: {
    ui: { type: "primary" },
    title: languages.continue,
    value: -1,
  },
}

export const patient: Question = {
  title: languages.questionForWho,
  id: PATH.patient,
  ui: { preventAddMore: true },
  multipleReplies: false,
  replies: [],
}

export const pregnant: Question = {
  imgUrl: cdn("/images/enceinte_illustration.svg"),
  title: languages.anamnesePregnantQuestion,
  id: PATH.pregnant,
  multipleReplies: false,
  replies: booleanReplies,
}

export const operation: Question = {
  imgUrl: cdn("/images/operation_illustration.svg"),
  title: languages.anamneseOperationsQuestion,
  id: PATH.operation,
  multipleReplies: false,
  replies: booleanReplies,
}

export const drug: Question = {
  imgUrl: cdn("/images/medicament_illustration.svg"),
  title: languages.anamneseDrugsQuestion,
  id: PATH.drug,
  multipleReplies: false,
  replies: booleanSubReplies,
}
export const allergy_details: Question = {
  title: languages.anamneseAllergiesQuestionDetails,
  id: PATH.allergy_details,
  // ui: { view: "checkbox" },
  multipleReplies: true,
  replies: [
    {
      title: languages.penicillinAllergy,
      value: "allergie à la pénicilline",
      imgUrl: cdn("/images/allergies/picto_medicament.svg"),
    },
    {
      title: languages.drugAllergy,
      value: "allergie aux médicaments",
      imgUrl: cdn("/images/allergies/picto_medicament.svg"),
    },
    {
      title: languages.pollenAllergy,
      value: "allergie au pollen",
      imgUrl: cdn("/images/allergies/picto_pollen.svg"),
    },
    {
      title: languages.foodAllergy,
      value: "allergie alimentaire",
      imgUrl: cdn("/images/allergies/picto_alimentaire.svg"),
    },
    {
      title: languages.otherAllergy,
      value: "autre",
      imgUrl: cdn("/images/allergies/picto_autre.svg"),
    },
  ],
}

export const allergy: Question = {
  imgUrl: cdn("/images/allergie_illustration.svg"),
  title: languages.anamneseAllergiesQuestion,
  subtitle: languages.anamneseAllergiesQuestionSubtitle,
  id: PATH.allergy,
  multipleReplies: false,
  subquestion: allergy_details,
  replies: booleanSubReplies,
}

export const illness_details: Question = {
  title: languages.anamneseIllnessQuestionDetails,
  id: PATH.illness_details,
  multipleReplies: true,
  // ui: { view: "checkbox" },
  replies: [
    {
      title: languages.diabetes,
      value: "diabète",
      imgUrl: cdn("/images/illnesses/picto_diabete.svg"),
    },
    {
      title: languages.hypertension,
      value: "hypertension",
      imgUrl: cdn("/images/illnesses/picto_hypertension.svg"),
    },
    {
      title: languages.cancer,
      value: "cancer",
      imgUrl: cdn("/images/illnesses/picto_cancer.svg"),
    },
    {
      title: languages.respiratoryDisease,
      value: "maladie respiratoire",
      imgUrl: cdn("/images/illnesses/picto_respiration.svg"),
    },
    {
      title: languages.heartDisease,
      value: "maladie cardiaque",
      imgUrl: cdn("/images/illnesses/picto_cardiaque.svg"),
    },
    {
      title: languages.kidneyDisease,
      value: "maladie rénale",
      imgUrl: cdn("/images/illnesses/picto_renal.svg"),
    },
    {
      title: languages.digestiveDisease,
      value: "maladie digestive",
      imgUrl: cdn("/images/illnesses/picto_digestion.svg"),
    },
    {
      title: languages.infectiousDisease,
      value: "maladie infectieuse",
      imgUrl: cdn("/images/illnesses/picto_infection.svg"),
    },
    {
      title: languages.otherDisease,
      value: "autre",
      imgUrl: cdn("/images/allergies/picto_autre.svg"),
    },
  ],
}

export const illness: Question = {
  imgUrl: cdn("/images/maladie_chronique_illustration.svg"),
  title: languages.anamneseIllnessQuestion,
  subtitle: languages.anamneseIllnessQuestionSubtitle,
  id: PATH.illness,
  subquestion: illness_details,
  multipleReplies: false,
  replies: booleanSubReplies,
}

export const anamnesis_questions: Question & { questions: Question[] } = {
  id: PATH.anamnesis_questions,
  title: languages.dermatoAnamnesisQuestionsTitle,
  ui: { view: "questions_line" },
  questions: [illness, operation, allergy, drug],
  replies: [],
  multipleReplies: true,
}
export const QUESTIONS: Question[] = [
  patient,
  exemption,
  inform_anamnesis,
  reason,
  anamnesis_questions,
  pregnant,
  upload_documents,
  ineligible_reason,
  doctor_available,
  diagnostic_devices,
  choice_of_doctor,
]

// JSON SCHEMA
export const surveyLanguages = [
  {
    id: PATH.doctor_type,
    patient: {
      title: languages.chooseSpecialist,
    },
    relative: {
      male: { title: languages.chooseSpecialist },
      female: { title: languages.chooseSpecialist },
    },
  },
  {
    id: PATH.reason,
    patient: {
      title: languages.anamneseReasonQuestion,
    },
    relative: {
      male: { title: languages.anamneseReasonQuestion_relative_male },
      female: { title: languages.anamneseReasonQuestion_relative_female },
    },
  },

  {
    id: PATH.patient,
    patient: {
      title: languages.questionForWho,
    },
    relative: {
      male: { title: languages.questionForWho },
      female: { title: languages.questionForWho },
    },
  },
  {
    id: PATH.doctor,
    patient: {
      title: languages.orderForWhichDoctor,
      defaultReply: languages.noAppointment,
    },
    relative: {
      male: {
        title: languages.orderForWhichDoctor_relative_male,
        defaultReply: languages.noAppointment_relative_male,
      },
      female: {
        title: languages.orderForWhichDoctor_relative_female,
        defaultReply: languages.noAppointment_relative_female,
      },
    },
  },
  {
    id: PATH.slot,
    patient: {
      title: languages.questionWhichTime,
    },
    relative: {
      male: { title: languages.questionWhichTime },
      female: { title: languages.questionWhichTime },
    },
  },
  {
    id: PATH.pregnant,
    patient: {
      title: languages.anamnesePregnantQuestion,
    },
    relative: {
      male: { title: languages.anamnesePregnantQuestion_relative_female },
      female: { title: languages.anamnesePregnantQuestion_relative_female },
    },
  },
  {
    id: PATH.operation,
    patient: {
      title: languages.anamneseOperationsQuestion,
    },
    relative: {
      male: { title: languages.anamneseOperationsQuestion_relative_male },
      female: { title: languages.anamneseOperationsQuestion_relative_female },
    },
  },
  {
    id: PATH.doctor_available,
    patient: {
      title: languages.anamneseDoctorAvailableQuestion,
    },
    relative: {
      male: {
        title: languages.anamneseDoctorAvailableQuestion_relative_male,
        // Order for doctor_available.replies: [DOCTOR_AWAY_CONSENT, NO_DOCTOR, DOCTOR_UNAVAILABLE]
        replies: [
          languages.doctorFarAway_relative(Gender.MALE),
          languages.doNotHaveDoctor_relative(Gender.MALE),
          languages.doctorNotAvailable_relative(Gender.MALE),
        ],
      },
      female: {
        title: languages.anamneseDoctorAvailableQuestion_relative_female,
        // Order for doctor_available.replies: [DOCTOR_AWAY_CONSENT, NO_DOCTOR, DOCTOR_UNAVAILABLE]
        replies: [
          languages.doctorFarAway_relative(Gender.FEMALE),
          languages.doNotHaveDoctor_relative(Gender.FEMALE),
          languages.doctorNotAvailable_relative(Gender.FEMALE),
        ],
      },
    },
  },
  {
    id: PATH.drug,
    patient: {
      title: languages.anamneseDrugsQuestion,
    },
    relative: {
      male: { title: languages.anamneseDrugsQuestion_relative_male },
      female: { title: languages.anamneseDrugsQuestion_relative_female },
    },
  },
  {
    id: PATH.allergy,
    patient: {
      title: languages.anamneseAllergiesQuestion,
      subtitle: languages.anamneseAllergiesSubtitle,
    },
    relative: {
      male: {
        title: languages.anamneseAllergiesQuestion_relative_male,
        subtitle: languages.anamneseAllergiesSubtitle,
      },
      female: {
        title: languages.anamneseAllergiesQuestion_relative_female,
        subtitle: languages.anamneseAllergiesSubtitle,
      },
    },
  },
  {
    id: PATH.illness,
    patient: {
      title: languages.anamneseIllnessQuestion,
      subtitle: languages.anamneseIllnessSubtitle,
    },
    relative: {
      male: {
        title: languages.anamneseIllnessQuestion_relative_male,
        subtitle: languages.anamneseIllnessSubtitle,
      },
      female: {
        title: languages.anamneseIllnessQuestion_relative_female,
        subtitle: languages.anamneseIllnessSubtitle,
      },
    },
  },
  {
    id: PATH.inform,
    patient: {
      title: languages.inform_anamnesis,
    },
    relative: {
      male: { title: languages.inform_anamnesis_relative_male },
      female: { title: languages.inform_anamnesis_relative_female },
    },
  },
  {
    id: PATH.dermato_phototype,
    patient: {
      title: languages.dermatoPhototypeTitle,
    },
    relative: {
      male: { title: languages.dermatoPhototypeTitle_relative_male },
      female: { title: languages.dermatoPhototypeTitle_relative_female },
    },
  },
  {
    id: PATH.anamnesis_questions,
    patient: {
      title: languages.anamnesisQuestionsTitle,
    },
    relative: {
      male: { title: languages.anamnesisQuestionsTitle_relative_male },
      female: { title: languages.anamnesisQuestionsTitle_relative_female },
    },
  },
  {
    id: PATH.dermato_last_control,
    patient: {
      title: languages.dermatoLastControl,
    },
    relative: {
      male: { title: languages.dermatoLastControl_relative_male },
      female: { title: languages.dermatoLastControl_relative_female },
    },
  },
  {
    id: PATH.dermato_sun_exposure,
    patient: {
      title: languages.dermatoSunExposure,
    },
    relative: {
      male: { title: languages.dermatoSunExposure_relative_male },
      female: { title: languages.dermatoSunExposure_relative_female },
    },
  },
  {
    id: PATH.dermato_sun_protection,
    patient: {
      title: languages.dermatoSunProtection,
    },
    relative: {
      male: { title: languages.dermatoSunProtection_relative_male },
      female: { title: languages.dermatoSunProtection_relative_female },
    },
  },
  {
    id: PATH.dermato_sunburn_before_20,
    patient: {
      title: languages.dermatoSunburnBefore20,
    },
    relative: {
      male: { title: languages.dermatoSunburnBefore20_relative_male },
      female: { title: languages.dermatoSunburnBefore20_relative_female },
    },
  },
  {
    id: PATH.dermato_history,
    patient: {
      title: languages.dermatoHistory,
    },
    relative: {
      male: { title: languages.dermatoHistory_relative_male },
      female: { title: languages.dermatoHistory_relative_female },
    },
  },
  {
    id: PATH.dermato_family_cancer,
    patient: {
      title: languages.dermatoFamilyCancer,
    },
    relative: {
      male: { title: languages.dermatoFamilyCancer_relative_male },
      female: { title: languages.dermatoFamilyCancer_relative_female },
    },
  },
  {
    id: PATH.dermato_recent_travel,
    patient: {
      title: languages.dermatoRecentTravel,
    },
    relative: {
      male: { title: languages.dermatoRecentTravel_relative_male },
      female: { title: languages.dermatoRecentTravel_relative_female },
    },
  },
  {
    id: PATH.dermato_zone_evolution,
    patient: {
      title: languages.dermatoZoneEvolution,
    },
    relative: {
      male: { title: languages.dermatoZoneEvolution_relative_male },
      female: { title: languages.dermatoZoneEvolution_relative_female },
    },
  },
]

// /!\ DO NOT TRANSLATE THE REASON KEY VALUE IN THIS OBJECT /!\
export const subTitles = [
  {
    reason: "Coronavirus (Covid 19)",
    subtitle: languages.uploadDocumentTypesHints,
  },
  { reason: "Allergies", subtitle: languages.uploadDocumentTypesHints },
  { reason: "Brûlure", subtitle: languages.photoOfBurn },
  {
    reason: "Contraception",
    subtitle: languages.uploadDocumentTypesHints,
  },
  { reason: "Cystite", subtitle: languages.uploadDocumentTypesHints },
  {
    reason: "Douleurs articulaires",
    subtitle: languages.photoOfJoins,
  },
  {
    reason: "Douleurs dentaires",
    subtitle: languages.uploadDocumentTypesHints,
  },
  {
    reason: "Diarrhée ou vomissement",
    subtitle: languages.uploadDocumentTypesHints,
  },
  {
    reason: "Douleurs lombaires",
    subtitle: languages.uploadDocumentTypesHints,
  },
  { reason: "Éruption cutanée", subtitle: languages.photoOfLesion },
  {
    reason: "Fatigue / anxiété / stress",
    subtitle: languages.uploadDocumentTypesHints,
  },
  { reason: "Fièvre", subtitle: languages.uploadDocumentTypesHints },
  {
    reason: "Infection sexuelle",
    subtitle: languages.uploadDocumentTypesHints,
  },
  { reason: "Maux de gorge", subtitle: languages.photoOfThroat },
  { reason: "Maux de tête", subtitle: languages.uploadDocumentTypesHints },
  {
    reason: "Maux de ventre",
    subtitle: languages.uploadDocumentTypesHints,
  },
  { reason: "Œil rouge et/ou douleur", subtitle: languages.photoOfTheEye },
  { reason: "Piqûre / morsure / blessure", subtitle: languages.photoOfSting },
  { reason: "Oreilles/otite", subtitle: languages.uploadDocumentTypesHints },
  {
    reason: "Renouvellement d'ordonnance",
    subtitle: languages.PrescriptionToBeRenewed,
  },
  {
    reason: "Règles douloureuses",
    subtitle: languages.uploadDocumentTypesHints,
  },
  { reason: "Résultats de laboratoire", subtitle: languages.analysisResults },
  {
    reason: "Rhume et syndrome grippal",
    subtitle: languages.uploadDocumentTypesHints,
  },
  {
    reason: "Sinus ou nez bouché",
    subtitle: languages.uploadDocumentTypesHints,
  },
  { reason: "Toux", subtitle: languages.uploadDocumentTypesHints },
  {
    reason: "Autre",
    subtitle: languages.uploadDocumentTypesHints,
  },
]


/*
* INITIAL MODEL USED FOR PREVENTION REPORT SURVEY
*/

export enum BP_PATH {
  bp_info = "bp_info",
  prevention_intro = "prevention-intro-redirect",
  prevention_report_info = "prevention-report-info-redirect",
  prevention_pharmacist_confirm = "prevention_pharmacist_confirm",
  prevention_handoff = "prevention-handoff-redirect",
}

export const preventionPharmacistConfirmation: Question = {
  id: BP_PATH.prevention_pharmacist_confirm,
  title: languages.preventionPharmacistConfirmTitle,
  subtitle: languages.preventionPharmacistConfirmText,
  imgUrl: cdn("/images/warning_picto.svg"),
  multipleReplies: false,
  ui: { view: "button" },
  replies: [
    {
      title: languages.preventionPharmacistConfirmNo,
      value: "pharmacist_cancelled",
      ui: { type: "default", view: "button" },
    },
    {
      title: languages.preventionPharmacistConfirmYes,
      value: "pharmacist_confirmed",
      ui: { type: "primary", view: "button" },
    }
  ],
}

// First two presentation slides, before the pharmacist confirmation
const preventionIntro: Question = {
  title: BP_PATH.prevention_intro,
  id: BP_PATH.prevention_intro,
  replies: [],
  type: QUESTION_TYPE.redirect,
  value: PREVENTION_INTRO_PAGE,
}

const preventionReportInfo: Question = {
  title: 'prevention-report-info-redirect',
  id: BP_PATH.prevention_report_info,
  replies: [],
  type: QUESTION_TYPE.redirect,
  value: PREVENTION_REPORT_INFO,
}

// "Où souhaitez-vous répondre au questionnaire ?": phone (QR code) or kiosk
const preventionHandoff: Question = {
  title: BP_PATH.prevention_handoff,
  id: BP_PATH.prevention_handoff,
  replies: [],
  type: QUESTION_TYPE.redirect,
  value: PREVENTION_HANDOFF_PAGE,
}

const preventionReportLoader: Question = {
  title: 'prevention-report-loader:loader',
  id: 'prevention-report-loader',
  replies: [],
  type: QUESTION_TYPE.loader,
  value: ACTION.nothing
}

export const preventionDocumentLoader: Question = {
  title: 'prevention-document-loader',
  id: 'prevention-document-loader',
  replies: [],
  type: QUESTION_TYPE.loader,
  value: ACTION.nothing
}

export const preventionSurveyEnd: Question = {
  id: "prevention-survey-end",
  title: languages.preventionSurveyEndTitle,
  imgUrl: cdn("/images/tlc_terminee_illustration_blue.svg"),
  multipleReplies: false,
  ui: { view: "button" },
  replies: [
    {
      title: languages.preventionSurveyEndButton,
      value: "prevention_survey_end_confirmed",
      ui: { type: "primary", view: "button" },
    },
  ],
}

export const INITIAL_PREVENTION_QUESTIONS = [
  preventionIntro,
  preventionPharmacistConfirmation,
  preventionHandoff,
  preventionReportInfo,
  patient,
  exemption,
  preventionReportLoader,
]